import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { GetDriveAccessRequestResponse } from '@dreamai/types';
import api from '../api';
import DriveAccessPrompt from './common/DriveAccessPrompt';
import {
  DriveAccessRequestData,
  useGoogleDrivePicker,
} from './common/useGoogleDrivePicker';

/**
 * Landing page for the grant-access link the assistant sends over channels
 * outside the app (e.g. WhatsApp): `/grant-access/<requestId>`.
 *
 * Google's Picker needs the user's session, so this route is authenticated —
 * an unauthenticated visitor is sent through login and returned here. It loads
 * the saved request and shows the same consent prompt, from which Google's
 * Picker opens on the user's click.
 */
const GrantDriveAccessPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [request, setRequest] = useState<DriveAccessRequestData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [declined, setDeclined] = useState(false);
  const { opening, pickerVisible, pickerError, openPicker, cancelRequest } =
    useGoogleDrivePicker({
      onPicked: () => setDone(true),
      onCancelled: () => setDeclined(true),
    });

  useEffect(() => {
    if (!requestId) return;
    let stale = false;
    const load = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetDriveAccessRequestResponse>(
          `/google-link/drive-access-requests/${requestId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (stale) return;
        if (data.request.status !== 'pending') {
          setLoadError(
            'This access request has already been dealt with. You can close this page.',
          );
          return;
        }
        setRequest({
          requestId: data.request.id,
          threadId: data.request.threadId,
          agentId: data.request.agentId,
          oauthId: data.request.oauthId,
          fileType: data.request.fileType,
          fileName: data.request.fileName ?? null,
        });
      } catch (err) {
        console.error('[GrantDriveAccess] Failed to load request:', err);
        if (!stale) {
          setLoadError(
            'We could not find that access request. Ask your secretary to send a new link.',
          );
        }
      }
    };
    void load();
    return () => {
      stale = true;
    };
  }, [requestId, getAccessTokenSilently]);

  // Await the cancel: the app root refetches outstanding requests on load, so
  // navigating first would race the update and re-show this same prompt.
  const dismiss = useCallback(async () => {
    if (request) await cancelRequest(request);
    navigate('/', { replace: true });
  }, [request, cancelRequest, navigate]);

  if (loadError || done || declined) {
    return (
      <div className='grant-drive-access-page'>
        <p>
          {done &&
            'Thanks — your secretary now has access and is picking the task back up.'}
          {declined &&
            !done &&
            'No file was selected, so your secretary will not open one. You can close this page.'}
          {!done && !declined && loadError}
        </p>
        <button type='button' onClick={() => navigate('/', { replace: true })}>
          Go to Dream
        </button>
      </div>
    );
  }

  // Nothing but Google's Picker on screen while it is open: a modal over it
  // covers it and swallows taps.
  if (!request || pickerVisible) {
    return <div className='grant-drive-access-page' />;
  }

  return (
    <DriveAccessPrompt
      request={request}
      opening={opening}
      error={pickerError}
      onCancel={dismiss}
      onChoose={() => void openPicker(request)}
    />
  );
};

export default GrantDriveAccessPage;
